import { useEffect, useState } from "react";
import api, { formatError, formatRupiah } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Wallet, Banknote, CreditCard, Loader2 } from "lucide-react";

const emptyForm = { name: "", type: "tunai", balance: "" };

export default function Money() {
  const [sources, setSources] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const fetchSources = () => api.get("/money-sources").then((res) => setSources(res.data)).catch(() => setSources([]));
  useEffect(() => { fetchSources(); }, []);

  const list = sources || [];
  const totalTunai = list.filter((s) => s.type === "tunai").reduce((a, s) => a + s.balance, 0);
  const totalNonTunai = list.filter((s) => s.type === "non-tunai").reduce((a, s) => a + s.balance, 0);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setFormOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, type: s.type, balance: String(s.balance) }); setFormOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { name: form.name, type: form.type, balance: parseFloat(form.balance) || 0 };
    try {
      if (editing) {
        await api.put(`/money-sources/${editing.id}`, payload);
        toast.success(`"${payload.name}" berhasil diperbarui`);
      } else {
        await api.post("/money-sources", payload);
        toast.success(`Tempat uang "${payload.name}" ditambahkan`);
      }
      setFormOpen(false);
      fetchSources();
    } catch (err) {
      toast.error(formatError(err, "Gagal menyimpan"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/money-sources/${deleting.id}`);
      toast.success(`"${deleting.name}" dihapus`);
      setDeleting(null);
      fetchSources();
    } catch (err) {
      toast.error(formatError(err, "Gagal menghapus"));
    }
  };

  if (sources === null) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="money-loading">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="money-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1">Total Kekayaan Kas</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">Uang Saya</h1>
          <p className="text-sm text-slate-500 mt-1">Catat semua tempat uang Anda — dompet tunai, rekening bank, e-wallet — lalu perbarui saldonya kapan saja.</p>
        </div>
        <Button onClick={openAdd} data-testid="add-source-button" className="bg-indigo-600 hover:bg-indigo-700 text-white transition-colors duration-200">
          <Plus className="w-4 h-4 mr-2" /> Tambah Tempat Uang
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-slate-200 shadow-sm bg-indigo-600 border-indigo-600" data-testid="total-uang-card">
          <CardContent className="p-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-indigo-200">Total Uang Saya</p>
            <p className="text-2xl font-heading font-bold text-white mt-2" data-testid="total-uang-value">{formatRupiah(totalTunai + totalNonTunai)}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm" data-testid="total-tunai-card">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
              <Banknote className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Uang Tunai</p>
              <p className="text-xl font-heading font-bold text-emerald-600" data-testid="total-tunai-value">{formatRupiah(totalTunai)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm" data-testid="total-nontunai-card">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center shrink-0">
              <CreditCard className="w-5 h-5 text-sky-600" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Non-Tunai</p>
              <p className="text-xl font-heading font-bold text-sky-600" data-testid="total-nontunai-value">{formatRupiah(totalNonTunai)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-3" data-testid="sources-list">
        {list.length === 0 ? (
          <Card className="border-slate-200 shadow-sm" data-testid="sources-empty">
            <CardContent className="p-12 text-center">
              <Wallet className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-400">Belum ada tempat uang. Klik Tambah Tempat Uang untuk mulai mencatat.</p>
            </CardContent>
          </Card>
        ) : (
          list.map((s) => (
            <Card key={s.id} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200" data-testid={`source-row-${s.id}`}>
              <CardContent className="p-5 flex flex-wrap items-center gap-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${s.type === "tunai" ? "bg-emerald-50" : "bg-sky-50"}`}>
                  {s.type === "tunai" ? <Banknote className="w-5 h-5 text-emerald-600" /> : <CreditCard className="w-5 h-5 text-sky-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{s.name}</p>
                  <Badge variant="secondary" className={s.type === "tunai" ? "bg-emerald-50 text-emerald-600 hover:bg-emerald-50" : "bg-sky-50 text-sky-600 hover:bg-sky-50"}>
                    {s.type === "tunai" ? "Tunai" : "Non-Tunai"}
                  </Badge>
                </div>
                <p className="text-lg font-heading font-bold text-slate-900 shrink-0 ml-auto" data-testid={`source-balance-${s.id}`}>{formatRupiah(s.balance)}</p>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="icon" data-testid={`edit-source-button-${s.id}`} onClick={() => openEdit(s)} title="Edit" className="text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-colors">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" data-testid={`delete-source-button-${s.id}`} onClick={() => setDeleting(s)} title="Hapus" className="text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="sm:max-w-md" data-testid="source-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading" data-testid="source-form-title">{editing ? `Edit: ${editing.name}` : "Tambah Tempat Uang"}</DialogTitle>
            <DialogDescription>{editing ? "Ubah nama, jenis, atau saldo tempat uang ini." : "Contoh: Dompet Tunai, Rekening BCA, GoPay, OVO."}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="source-form">
            <div className="space-y-2">
              <Label htmlFor="source-name">Nama Tempat Uang</Label>
              <Input id="source-name" data-testid="source-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Contoh: Rekening BCA" required className="focus-visible:ring-indigo-500" />
            </div>
            <div className="space-y-2">
              <Label>Jenis</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="source-type-select" className="focus:ring-indigo-500"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tunai" data-testid="source-type-tunai">Tunai (Cash)</SelectItem>
                  <SelectItem value="non-tunai" data-testid="source-type-non-tunai">Non-Tunai (Bank / E-Wallet)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="source-balance">Saldo Saat Ini (Rp)</Label>
              <Input id="source-balance" type="number" min="0" data-testid="source-balance-input" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} required className="focus-visible:ring-indigo-500" />
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" data-testid="source-form-cancel" onClick={() => setFormOpen(false)}>Batal</Button>
              <Button type="submit" data-testid="source-form-submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white transition-colors">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Simpan Perubahan" : "Tambah"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-source-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Hapus Tempat Uang?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold">{deleting?.name}</span> (saldo {formatRupiah(deleting?.balance)}) akan dihapus permanen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-source-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-source-confirm" onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700 text-white transition-colors">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
